import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Form from "@/components/ui/Form";
import ViewWrapper from "@/components/ui/ViewWrapper";
import { useToast } from "@/hooks/useToast";
import { RegisterSchema, RegisterType } from "./form";
import { SupabaseInstance } from "@/integrations/supabase";
import { Logger } from "@/utils";
import { ROUTES_PATH } from "@/router";
import { TOAST_MESSAGES } from "./constants";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterType>({
    resolver: zodResolver(RegisterSchema),
  });
  const onSubmit = handleSubmit((data) => {
    setIsLoading(true);
    Logger.addBreadcrumb({
      category: "auth",
      message: "signup_attempt",
      level: "info",
    });
    SupabaseInstance.signUp(data.email, data.password)
      .then(({ data, error }) => {
        if (error) {
          Logger.loggerError({
            section: "register_signup_error",
            message: error,
          });
          handleShowToast("GENERIC_ERROR");
          return;
        }
        const userMetaDataExist =
          data?.user &&
          data?.user?.user_metadata &&
          Object.keys(data?.user?.user_metadata).length > 0;
        if (userMetaDataExist) {
          handleShowToast("SUCCESS");
        } else {
          handleShowToast("ACCOUNT_EXISTS");
        }
        navigate(ROUTES_PATH.LOGIN);
      })
      .catch((err) => {
        Logger.loggerError({
          section: "register_signup_exception",
          message: err,
        });
        handleShowToast("SERVER_ERROR");
      })
      .finally(() => {
        setIsLoading(false);
      });
  });

  const handleShowToast = useCallback(
    (type: keyof typeof TOAST_MESSAGES) => {
      toast(TOAST_MESSAGES[type]);
    },
    [toast]
  );

  return (
    <ViewWrapper>
      <div className="max-w-md w-full mx-auto p-6 bg-white rounded-lg shadow-md border border-gray-200">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-startfly-black mb-2">
            Crear Cuenta
          </h1>
          <p className="text-startfly-gray">
            Crea tu cuenta para comenzar a generar activos de ventas
          </p>
        </div>
        <Form.ErrorView
          isVisible={
            !!(errors.email || errors.password || errors.confirmPassword)
          }
          title={
            errors.email?.message ||
            errors.password?.message ||
            errors.confirmPassword?.message
          }
        />
        <Form.Container onSubmit={onSubmit}>
          <Form.Field
            {...register("email")}
            label="Correo Electrónico"
            labelHtmlFor="email"
            id="email"
            type="email"
            placeholder="tu@email.com"
            error={errors.hasOwnProperty("email")}
          />
          <Form.Field
            {...register("password")}
            label="Contraseña"
            labelHtmlFor="password"
            id="password"
            type="password"
            placeholder="••••••••"
            error={errors.hasOwnProperty("password")}
          />
          <Form.Field
            {...register("confirmPassword")}
            label="Confirmar Contraseña"
            labelHtmlFor="confirmPassword"
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            error={errors.hasOwnProperty("confirmPassword")}
          />
          <Form.SubmitButton
            isLoading={isLoading}
            title="Crear Cuenta"
            loadingText="Creando cuenta..."
          />
        </Form.Container>
        <Form.FooterAction
          title="¿Ya tienes una cuenta?"
          buttonText="Inicia sesión"
          onClick={() => navigate(ROUTES_PATH.LOGIN)}
        />
      </div>
    </ViewWrapper>
  );
}
